import { useState } from 'react';
import { motion } from 'framer-motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { useLang } from '../lib/lang';
import { submitTestimonial } from '../lib/api';
import { Trophy, Sparkles, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Popup that asks the winner to write a testimonial.
 * Triggers from AccountPage when user has a winner record without an approved
 * (or pending) testimonial. Cannot be dismissed unless they submit OR close.
 * The popup will reappear on next account page visit if testimonial is still missing.
 */
export default function TestimonialPromptDialog({ open, winner, onClose, onSubmitted }) {
  const { t } = useLang();
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (text.trim().length < 5) {
      toast.error(t('testimonialTooShort') || 'Please write at least 5 characters');
      return;
    }
    setSubmitting(true);
    try {
      await submitTestimonial(winner._id, text.trim());
      setSubmitted(true);
      toast.success(t('testimonialSubmitted') || '✨ Submitted for review!');
      setTimeout(() => {
        onSubmitted && onSubmitted();
      }, 2200);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('toastFailed') || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!winner) return null;
  const data = winner;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="bg-white border-border max-w-lg overflow-hidden p-0 [&>button]:hidden"
        style={{ color: '#0B3D2E' }}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {!submitted ? (
          <>
            {/* Header with green/gold gradient */}
            <div className="relative bg-gradient-to-br from-[#0E4936] to-[#08291F] p-6 text-center">
              <div className="absolute inset-0 hero-spotlight pointer-events-none" />
              <motion.div
                initial={{ scale: 0.6, rotate: -15 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 200 }}
                className="relative inline-flex w-16 h-16 rounded-full bg-gold-gradient items-center justify-center mb-3 shadow-lg"
              >
                <Trophy className="w-8 h-8 text-rolex-green" />
              </motion.div>
              <p className="gold-line mb-2 mx-auto justify-center"><Sparkles className="w-3 h-3" /> Your story matters</p>
              <DialogTitle className="font-display text-2xl font-extrabold tracking-tight" style={{ color: '#F5EFE0' }}>
                {t('testimonialDialogTitle') || 'Share your experience!'}
              </DialogTitle>
              <DialogDescription className="text-sm mt-2 max-w-md mx-auto" style={{ color: 'rgba(245, 239, 224, 0.78)' }}>
                {t('testimonialDialogDesc') || `You won ${data.raffle_title}! Tell future winners how it feels — your testimonial will be displayed (after admin review) on the Winners page.`}
              </DialogDescription>
            </div>

            {/* Form */}
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <label className="text-xs uppercase tracking-[0.18em] text-rolex-green/60 font-bold">
                  {t('testimonialLabel2') || 'Your testimonial'} ({text.length}/1000)
                </label>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value.slice(0, 1000))}
                  placeholder={t('testimonialPlaceholder') || "e.g. 'I never thought I'd actually win — this watch is a dream! Thank you WINLUXOR!'"}
                  className="w-full bg-white border border-[hsl(var(--gold)/0.35)] rounded-xl p-4 text-sm resize-none !text-rolex-green focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold)/0.4)] h-32"
                  data-testid="testimonial-textarea"
                />
                <p className="text-[11px] text-rolex-green/55">
                  💡 {t('testimonialHint') || 'Min 5, max 1000 characters. Will be reviewed by our team before going public.'}
                </p>
              </div>
            </div>

            <DialogFooter className="px-6 pb-6 gap-2">
              <Button variant="outline" onClick={onClose} className="rounded-xl">
                {t('later') || 'Later'}
              </Button>
              <Button
                className="btn-gold rounded-xl gap-1.5 font-bold"
                onClick={handleSubmit}
                disabled={submitting || text.trim().length < 5}
                data-testid="submit-testimonial-btn"
              >
                <Send className="w-4 h-4" />
                {submitting ? (t('submitting') || 'Submitting...') : (t('submitTestimonial') || 'Submit')}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            className="p-10 text-center bg-gradient-to-br from-[#0E4936] to-[#08291F]"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', delay: 0.2 }}
              className="inline-flex w-16 h-16 rounded-full bg-green-500 items-center justify-center mb-4 shadow-lg"
            >
              <CheckCircle2 className="w-9 h-9 text-white" strokeWidth={2.5} />
            </motion.div>
            <h2 className="font-display text-2xl font-extrabold mb-2" style={{ color: '#F5EFE0' }}>
              {t('testimonialThanks') || 'Thank you!'}
            </h2>
            <p className="text-sm max-w-sm mx-auto" style={{ color: 'rgba(245, 239, 224, 0.78)' }}>
              {t('testimonialThanksDesc') || "Your testimonial has been submitted to our team for review. It will appear on the public Winners page once approved."}
            </p>
          </motion.div>
        )}
      </DialogContent>
    </Dialog>
  );
}
